import { useState, useEffect } from "react";
import { X, Linkedin, Users } from "lucide-react";
import type { Company, CompanyStatus } from "../api/HiringContactsApi";
import { COMPANY_STATUS_OPTIONS } from "../api/HiringContactsApi";
import { COMPANY_STATUS_CHIP } from "./CompanyAssignmentCard";
import { WatchToggle, PriorityBadge } from "./ContactCard";
import TechTagChip from "shared/TechTagChip";
import { useHiringContacts } from "../modal/HiringContactsContext";
import StatusNotePopover from "shared/StatusNotePopover";

const TERMINAL = new Set(["archived", "not_relevant"]);

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

interface Props {
  company: Company;
  isAdmin: boolean;
  onClose: () => void;
  /** Opens the Assign modal for this company (where TMs can be added or removed) —
   * only wired up for admins; there's no standalone "Unassign" action here anymore. */
  onManageAssignees?: () => void;
}

export default function CompanyAssignmentDetailModal({ company, isAdmin, onClose, onManageAssignees }: Props) {
  const {
    handleCompanyStatusUpdate, handleCompanyWatchToggle, handleCompanyPriorityChange,
    loadCompanyHistory, companyHistory, companyHistoryLoading, assignableEmployees,
  } = useHiringContacts();

  const assignedEmployee = isAdmin && company.employee_id
    ? assignableEmployees.find((e) => e.id === company.employee_id) ?? null
    : null;

  // A company can be assigned to more than one TM at once, each tracking it
  // independently — this modal's own assignee (above) plus everyone else working it.
  const otherAssignees = isAdmin
    ? (company.assigned_employees ?? []).filter((a) => a.employee_id !== company.employee_id)
    : [];

  const [localStatus, setLocalStatus] = useState<CompanyStatus>(company.status ?? "new");
  const [pendingStatus, setPendingStatus] = useState<CompanyStatus | null>(null);
  const [pendingNotes, setPendingNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isTerminal = TERMINAL.has(localStatus);

  useEffect(() => {
    if (company.assignment_id) loadCompanyHistory(company.assignment_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.assignment_id]);

  function selectStatus(status: CompanyStatus) {
    if (isTerminal || status === localStatus) {
      setPendingStatus(null);
      setPendingNotes("");
      setSaveError(null);
      return;
    }
    setPendingStatus(status);
    setPendingNotes("");
    setSaveError(null);
  }

  function cancelPending() {
    setPendingStatus(null);
    setPendingNotes("");
    setSaveError(null);
  }

  async function confirmStatusChange() {
    if (!company.assignment_id || !pendingStatus) return;
    setSaving(true);
    setSaveError(null);
    try {
      await handleCompanyStatusUpdate(company.assignment_id, pendingStatus, pendingNotes.trim() || null);
      setLocalStatus(pendingStatus);
      setPendingStatus(null);
      setPendingNotes("");
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 p-6 border-b border-border shrink-0">
          <div className="w-12 h-12 rounded-lg border border-border bg-surface-muted flex items-center justify-center shrink-0">
            <span className="text-lg font-bold text-primary font-inter">
              {company.name.charAt(0).toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground font-inter leading-snug inline-flex items-center gap-1.5">
              {company.name}
              {company.linkedin_url && (
                <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-400 shrink-0">
                  <Linkedin className="w-3.5 h-3.5" />
                </a>
              )}
            </h2>
            <p className="text-xs text-muted-foreground font-inter mt-0.5">{company.domain}</p>
            {company.tech_tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {company.tech_tags.slice(0, 6).map((tag) => (
                  <TechTagChip key={tag} label={tag} />
                ))}
              </div>
            )}
            {isAdmin && (assignedEmployee || otherAssignees.length > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {assignedEmployee && (
                  <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-surface-muted text-foreground font-inter font-medium">
                    {assignedEmployee.name}
                  </span>
                )}
                {otherAssignees.length > 0 && (
                  <>
                    <span className="text-[10px] text-muted-foreground font-inter">Also assigned:</span>
                    {otherAssignees.map((a) => (
                      <span key={a.employee_id} className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-surface-muted/60 text-muted-foreground font-inter font-medium">
                        {a.name}
                      </span>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <PriorityBadge priority={company.priority} onChange={(p) => handleCompanyPriorityChange(company.id, p)} />
          <WatchToggle
            isWatched={company.is_watched}
            onToggle={() => handleCompanyWatchToggle(company.id, !company.is_watched)}
            disabled={!company.linkedin_url}
          />
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors shrink-0 self-start"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status tracking */}
        <div className="px-6 pt-4 pb-4 shrink-0">
          <span className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter mb-2">
            Engagement Tracking
          </span>

          {isTerminal ? (
            <div className="flex items-center gap-2">
              <StatusNotePopover
                note={company.assignment_notes}
                fromLabel={company.assignment_from_status ? COMPANY_STATUS_CHIP[company.assignment_from_status]?.label : null}
                toLabel={COMPANY_STATUS_CHIP[localStatus]?.label ?? localStatus}
              >
                <span className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium ${COMPANY_STATUS_CHIP[localStatus]?.className ?? "bg-muted text-foreground border-border"} ${company.assignment_notes ? "cursor-help" : ""}`}>
                  {COMPANY_STATUS_CHIP[localStatus]?.label ?? localStatus}
                </span>
              </StatusNotePopover>
              <p className="text-xs text-muted-foreground font-inter italic">
                This company is in a terminal state and cannot be updated.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {COMPANY_STATUS_OPTIONS.map((opt) => {
                const isCurrent = localStatus === opt.value;
                const isPending = pendingStatus === opt.value;
                return (
                  <StatusNotePopover
                    key={opt.value}
                    note={isCurrent && !isPending ? company.assignment_notes : null}
                    fromLabel={company.assignment_from_status ? COMPANY_STATUS_CHIP[company.assignment_from_status]?.label : null}
                    toLabel={opt.label}
                  >
                    <button
                      onClick={() => selectStatus(opt.value)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-all ${
                        isPending
                          ? `${COMPANY_STATUS_CHIP[opt.value]?.className ?? "bg-muted text-foreground border-border"} ring-2 ring-white/20 ring-offset-1 ring-offset-surface`
                          : isCurrent
                          ? COMPANY_STATUS_CHIP[opt.value]?.className ?? "bg-muted text-foreground border-border"
                          : "bg-transparent text-muted-foreground border-border hover:border-border hover:text-foreground"
                      }`}
                    >
                      {opt.label}
                    </button>
                  </StatusNotePopover>
                );
              })}
            </div>
          )}

          {pendingStatus && !isTerminal && (
            <div className="mt-3 space-y-2">
              <textarea
                value={pendingNotes}
                onChange={(e) => setPendingNotes(e.target.value)}
                placeholder="Add a note for this status change… (optional)"
                rows={2}
                autoFocus
                className="w-full bg-surface-muted/50 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder-muted-foreground font-inter resize-none focus:outline-none focus:border-primary/60 transition-colors"
              />
              {saveError && <p className="text-[11px] text-red-400 font-inter">{saveError}</p>}
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmStatusChange}
                  disabled={saving}
                  className="px-3 py-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
                >
                  {saving ? "Saving…" : `Save as "${COMPANY_STATUS_CHIP[pendingStatus]?.label ?? pendingStatus}"`}
                </button>
                <button
                  onClick={cancelPending}
                  className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-xs font-inter rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* History — always shown inline below the tracking status list, not gated
            behind a click, since there's little else in this modal to compete with
            it for space. */}
        <div className="px-6 pb-4 shrink-0">
          <div className="border border-border rounded-2xl overflow-hidden bg-surface-muted/20">
            <div className="px-4 py-2 border-b border-border">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground font-inter">
                Status History
              </span>
            </div>
            <div className="px-4 py-3 max-h-48 overflow-y-auto">
              {companyHistoryLoading ? (
                <p className="text-xs text-muted-foreground font-inter py-1">Loading…</p>
              ) : companyHistory.length === 0 ? (
                <p className="text-xs text-muted-foreground font-inter italic py-1">No status changes yet.</p>
              ) : (
                <div className="space-y-3">
                  {companyHistory.map((h, i) => (
                    <div key={h.id} className="relative pl-4">
                      {i < companyHistory.length - 1 && (
                        <span className="absolute left-[3px] top-3 bottom-[-8px] w-px bg-border" />
                      )}
                      <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-surface-muted" />
                      <div className="flex flex-wrap items-center gap-1.5">
                        {h.from_status && (
                          <>
                            <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${COMPANY_STATUS_CHIP[h.from_status]?.className ?? "text-muted-foreground border-transparent"}`}>
                              {COMPANY_STATUS_CHIP[h.from_status]?.label ?? h.from_status}
                            </span>
                            <span className="text-muted-foreground text-[10px]">→</span>
                          </>
                        )}
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] font-inter ${COMPANY_STATUS_CHIP[h.to_status]?.className ?? "text-muted-foreground border-transparent"}`}>
                          {COMPANY_STATUS_CHIP[h.to_status]?.label ?? h.to_status}
                        </span>
                        <span className="ml-auto text-[10px] text-muted-foreground font-inter shrink-0">
                          {formatDate(h.changed_at)}
                        </span>
                      </div>
                      {h.notes && (
                        <p className="text-[11px] text-muted-foreground font-inter mt-0.5 italic">"{h.notes}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border shrink-0">
          {company.linkedin_url ? (
            <a
              href={company.linkedin_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-inter font-medium rounded-xl transition-colors whitespace-nowrap"
            >
              <Linkedin className="w-4 h-4" />
              View on LinkedIn
            </a>
          ) : <span />}
          {onManageAssignees && (
            <button
              onClick={onManageAssignees}
              className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-inter transition-colors"
            >
              <Users className="w-4 h-4" />
              Manage Assignees
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
